<template>
    <header
        class="sticky top-0 z-30 h-12 text-white"
        :class="[
            network.isOnline ? 'bg-surface-header' : 'bg-danger',
            scannerActive ? 'bg-black/30 backdrop-blur-sm' : ''
        ]"
    >
        <div class="flex items-center px-3 h-full max-w-2xl mx-auto w-full">
            <button class="flex items-center gap-2 font-semibold" @click="router.push({ name: 'home' })">
                {{ t('app.name') }}
                <UserCheck class="w-5 h-5" />
            </button>

            <span
                v-if="queue.sendQueue.length"
                class="ml-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-danger text-white text-xs"
            >
                <AlertTriangle class="w-3.5 h-3.5" />
                {{ queue.sendQueue.length }}
            </span>

            <span class="flex-1" />

            <button v-if="auth.user" class="p-1 mr-4" :aria-label="t('verify.title')" @click="verify.open()">
                <Search class="w-5 h-5" />
            </button>

            <span v-if="!network.isOnline" class="text-sm font-bold mr-2">{{ t('app.networkNotAvailable') }}</span>
            <Wifi v-if="network.isOnline" class="w-5 h-5 text-success" />
            <WifiOff v-else class="w-5 h-5" />

            <div v-if="auth.user" class="relative ml-2">
                <button class="p-1" @click="menuOpen = !menuOpen">
                    <MoreVertical class="w-5 h-5" />
                </button>
                <div
                    v-if="menuOpen"
                    class="absolute right-0 top-full mt-1 bg-surface-card text-white rounded shadow-xl min-w-[200px] py-1 z-40"
                >
                    <button class="w-full text-left px-4 py-2 hover:bg-white/10" @click="startTest">
                        {{ t('menu.test') }}
                    </button>
                    <button class="w-full text-left px-4 py-2 hover:bg-white/10" @click="showSettings">
                        {{ t('menu.settings') }}
                    </button>
                    <hr class="border-white/10 my-1" />
                    <a class="block px-4 py-2 hover:bg-white/10" href="/admin/" target="_blank">
                        {{ t('menu.admin') }}
                    </a>
                    <button class="w-full text-left px-4 py-2 hover:bg-white/10" @click="resetState">
                        {{ t('menu.resetState') }}
                    </button>
                    <hr class="border-white/10 my-1" />
                    <button class="w-full text-left px-4 py-2 hover:bg-white/10" @click="logout">
                        {{ t('menu.logout') }}
                    </button>
                </div>
            </div>
        </div>
    </header>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue'
    import { useRouter } from 'vue-router'
    import { useAuthStore } from '@/stores/auth'
    import { useQueueStore } from '@/stores/queue'
    import { useNetworkStore } from '@/stores/network'
    import { useEventsStore } from '@/stores/events'
    import { useTicketsStore } from '@/stores/tickets'
    import { useMessagesStore } from '@/stores/messages'
    import { useSettingsStore } from '@/stores/settings'
    import { useVerifyStore } from '@/stores/verify'
    import { useI18n } from 'vue-i18n'
    import { initSound } from '@/services/sound'
    import { clearAllPersisted } from '@/services/persist/pinia-idb'
    import { Wifi, WifiOff, MoreVertical, AlertTriangle, UserCheck, Search } from 'lucide-vue-next'

    const auth = useAuthStore()
    const queue = useQueueStore()
    const network = useNetworkStore()
    const events = useEventsStore()
    const tickets = useTicketsStore()
    const messages = useMessagesStore()
    const settings = useSettingsStore()
    const verify = useVerifyStore()
    const router = useRouter()
    const { t } = useI18n()

    const route = router.currentRoute
    const scannerActive = computed(() => route.value.path.includes('scanner'))

    const menuOpen = ref(false)

    async function logout() {
        try {
            await auth.logoutUser()
        } catch {
            // ignore
        }
        void router.push({ name: 'home' })
        menuOpen.value = false
    }

    function startTest() {
        initSound()
        void router.push({ name: 'test-scanner' })
        menuOpen.value = false
    }

    function showSettings() {
        void router.push({ name: 'settings' })
        menuOpen.value = false
    }

    async function resetState() {
        await clearAllPersisted(['settings', 'events', 'tickets', 'queue', 'auth'])
        tickets.clearTickets()
        tickets.setLastCode('')
        tickets.updateLoopStop()
        events.events = []
        events.zones = []
        events.activeEvents = []
        events.activeTicketsetIds = []
        events.activeCheckpoint = null
        events.activeZones = []
        events.lastZones = null
        events.lastZoneLoadDate = null
        events.zoneStats = []
        queue.clear()
        messages.clearMessages()
        settings.reset()
        void router.push({ name: 'home' })
        menuOpen.value = false
    }
</script>
